import React from 'react';
import Card from '../../../../shared/Card';
import { ScaleIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { Badge } from '../../../../../ui/badge';
import { FOSOpinion } from '../../../../../../types/signals-live';

type Props = {
  prediction?: FOSOpinion | null;
  onOpen: () => void;
};

const outcomeLabel = (o?: string) => {
  switch (o) {
    case 'upheld':
      return 'Likely Upheld';
    case 'not_upheld':
      return 'Likely Not Upheld';
    case 'partially_upheld':
      return 'Likely Partially Upheld';
    default:
      return 'Unknown';
  }
};

const outcomeBadgeClasses = (o?: string) => {
  switch (o) {
    case 'upheld':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'not_upheld':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'partially_upheld':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const formatPct = (v?: number) => `${Math.round((v || 0) * 100)}%`;

const OmbudsmanPredictionTile: React.FC<Props> = ({ prediction, onOpen }) => {
  const disabled = !prediction;
  const summaryText = prediction?.reasoning || 'No Ombudsman analysis yet';

  return (
    <Card className={`${disabled ? 'opacity-70' : 'hover:shadow-sm hover:bg-gray-50'} transition-colors`}> 
      <button
        type="button"
        onClick={onOpen}
        disabled={disabled}
        className="w-full text-left focus:outline-none cursor-pointer disabled:cursor-default"
        aria-label="Open Ombudsman Advisor"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 mb-2">
            <ScaleIcon className="w-4 h-4 text-gray-700" />
            <span className="text-sm font-semibold text-gray-900">Ombudsman Prediction</span>
          </div>
          <ChevronRightIcon className="w-4 h-4 text-gray-400" />
        </div>

        <div className="flex items-center gap-2 mb-2">
          <Badge variant="outline" className={outcomeBadgeClasses(prediction?.predicted_outcome)}>
            {outcomeLabel(prediction?.predicted_outcome)}
          </Badge>
          {prediction && (
            <Badge variant="outline" className="bg-blue-50 border-blue-200 text-blue-700">
              {formatPct(prediction?.confidence_score)} confidence
            </Badge>
          )}
        </div>

        <p className={`text-sm text-gray-700 ${prediction ? 'line-clamp-2' : ''}`}>
          {summaryText}
        </p>
      </button>
    </Card>
  );
};

export default React.memo(OmbudsmanPredictionTile);
